import { useState, useMemo, useEffect } from 'react';
import '../../assets/css/case-property.css';
import cornerIcon from '../../assets/images/case-image-corner.svg';
import caseBackgroundIcon from '../../assets/images/case-background.svg';
import caseVerticalIcon from '../../assets/images/case-vertical-icon.svg';
// @ts-ignore
import { useProperty, useContentToken, EditableToken, useCollectionBinding, useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
// @ts-ignore
import type { ComponentContentSchema, CollectionBindingDefinition, SlotGroupDefinition } from '@vibesites/runtime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faChevronDown, faDollarSign, faBed, faBath } from '@fortawesome/free-solid-svg-icons';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { PropertyContentSection } from './PropertyContentSection';
import { getPropertyBasePath } from './propertyUtils';
import { openTourScheduler } from './TourSchedulerModal';
import { RangeSlider } from './RangeSlider';
import { FlexPaymentBanner } from './FlexPaymentBanner';

// Slot definitions for bedroom section images
const FP_SLOT_DEFS: Record<string, SlotGroupDefinition> = {
  'propertyfloorplanslist.bedroom1_image': {
    label: '1-Bedroom Section Image',
    fields: {
      image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
      alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
    },
    sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
  },
  'propertyfloorplanslist.bedroom3_image': {
    label: '3-Bedroom Section Image',
    fields: {
      image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
      alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
    },
    sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
  },
  'propertyfloorplanslist.site_plan_bg': {
    // Decorative background — no alt field; the image renders with alt="" so
    // assistive tech skips it (the heading + CTA carry the section's meaning).
    label: 'Site Plan Background Image',
    fields: {
      image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    },
    sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image' } },
  },
  'propertyfloorplanslist.card_apply_cta': {
    label: 'Floor Plan Card Apply CTA',
    fields: {
      cta_text: { label: 'CTA Text', type: 'text', defaultValue: 'Apply Online' },
      cta_url: { label: 'CTA URL', type: 'url', defaultValue: '', defaultField: 'property_apply_now_url' },
      _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
      vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'apply_now_clicked' },
    },
  },
  'propertyfloorplanslist.card_tour_cta': {
    label: 'Floor Plan Card Tour CTA',
    fields: {
      cta_text: { label: 'CTA Text', type: 'text', defaultValue: 'Schedule a Tour' },
      cta_url: { label: 'CTA URL', type: 'url', defaultValue: '' },
      _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
      vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'schedule_tour_clicked' },
    },
  },
  'propertyfloorplanslist.site_plan_cta': {
    label: 'Site Plan CTA',
    fields: {
      cta_text: { label: 'CTA Text', type: 'text', defaultValue: 'View Our Property Map' },
      cta_url: { label: 'CTA URL', type: 'url', defaultValue: '' },
      _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
      vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: '' },
    },
  },
  'propertyfloorplanslist.bed3_cta': {
    label: '3-Bedroom Section CTA',
    fields: {
      cta_text: { label: 'CTA Text', type: 'text', defaultValue: 'Schedule a Tour' },
      cta_url: { label: 'CTA URL', type: 'url', defaultValue: '' },
      _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
      vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'schedule_tour_clicked' },
    },
  },
};

type SortField = 'beds' | 'price' | 'sqft';
type SortOrder = 'asc' | 'desc';

interface FilterState {
  minPrice: string;
  maxPrice: string;
  beds: string;
  baths: string;
  sortField: SortField;
  sortOrder: SortOrder;
}

export function PropertyFloorplansList() {
  const property = useProperty();
  const allFloorPlans = useCollectionBinding<any>('propertyfloorplanslist.plans', { collection: 'floorplans' });
  const basePath = getPropertyBasePath(property);
  const name = property.property_name || '';
  const phone = property.property_phone || '';
  const logo = property.property_primary_logo || property.property_mono_white_logo || property.property_secondary_logo || '';

  const bed1CtaText = useContentToken('propertyfloorplanslist.bed1_cta_text');
  const bed1CtaUrl = useContentToken('propertyfloorplanslist.bed1_cta_url');

  const sitePlanTitle = useContentToken('propertyfloorplanslist.site_plan_title') || `${name} Site Plan`;

  const bed1Image = useSlotGroup('propertyfloorplanslist.bedroom1_image', FP_SLOT_DEFS['propertyfloorplanslist.bedroom1_image']);
  const bed3Image = useSlotGroup('propertyfloorplanslist.bedroom3_image', FP_SLOT_DEFS['propertyfloorplanslist.bedroom3_image']);
  const sitePlanBg = useSlotGroup('propertyfloorplanslist.site_plan_bg', FP_SLOT_DEFS['propertyfloorplanslist.site_plan_bg']);
  const cardApplyCta = useSlotGroup('propertyfloorplanslist.card_apply_cta', FP_SLOT_DEFS['propertyfloorplanslist.card_apply_cta']);
  const cardTourCta = useSlotGroup('propertyfloorplanslist.card_tour_cta', FP_SLOT_DEFS['propertyfloorplanslist.card_tour_cta']);
  const sitePlanCta = useSlotGroup('propertyfloorplanslist.site_plan_cta', FP_SLOT_DEFS['propertyfloorplanslist.site_plan_cta']);
  const bed3Cta = useSlotGroup('propertyfloorplanslist.bed3_cta', FP_SLOT_DEFS['propertyfloorplanslist.bed3_cta']);

  // Derive price bounds from floorplan data
  const { priceMin, priceMax } = useMemo(() => {
    const rents: number[] = [];
    allFloorPlans.forEach((fp: any) => {
      for (const key of ['floorplan_price_min', 'floorplan_price_max']) {
        const raw = fp[key];
        if (raw == null || raw === '') continue;
        const cleaned = String(raw).replace(/[$,]/g, '');
        const r = parseFloat(cleaned);
        if (isFinite(r) && r > 0) rents.push(r);
      }
    });
    if (rents.length === 0) return { priceMin: 0, priceMax: 3000 };
    const min = Math.floor(Math.min(...rents) / 50) * 50;
    const max = Math.ceil(Math.max(...rents) / 50) * 50;
    return { priceMin: min, priceMax: max === min ? max + 50 : max };
  }, [allFloorPlans]);

  const allBeds = useMemo(() => {
    const beds = new Set<number>();
    allFloorPlans.forEach((fp: any) => {
      if (fp.floorplan_beds_min != null) beds.add(Number(fp.floorplan_beds_min));
    });
    return Array.from(beds).sort((a, b) => a - b);
  }, [allFloorPlans]);

  const allBaths = useMemo(() => {
    const baths = new Set<number>();
    allFloorPlans.forEach((fp: any) => {
      if (fp.floorplan_baths_min != null) baths.add(Number(fp.floorplan_baths_min));
    });
    return Array.from(baths).sort((a, b) => a - b);
  }, [allFloorPlans]);

  const has1Bed = allBeds.includes(1);
  const has2Bed = allBeds.includes(2);
  const has3PlusBed = allBeds.some(b => b >= 3);

  const [priceRange, setPriceRange] = useState<[number, number]>([priceMin, priceMax]);

  // Re-sync slider when price bounds change (async data load)
  useEffect(() => {
    setPriceRange(() => {
      // If the user hasn't moved the slider from defaults, track the new bounds
      // Otherwise keep their selection clamped within new bounds
      const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
      const urlMin = params.get('minPrice');
      const urlMax = params.get('maxPrice');
      if (urlMin || urlMax) {
        return [
          urlMin ? Math.max(Number(urlMin), priceMin) : priceMin,
          urlMax ? Math.min(Number(urlMax), priceMax) : priceMax,
        ];
      }
      return [priceMin, priceMax];
    });
  }, [priceMin, priceMax]);

  const isFullPriceRange = priceRange[0] === priceMin && priceRange[1] === priceMax;

  const [filters, setFilters] = useState<FilterState>(() => {
    const params = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
    return {
      minPrice: params.get('minPrice') || '',
      maxPrice: params.get('maxPrice') || '',
      beds: params.get('beds') || '',
      baths: params.get('baths') || '',
      sortField: (params.get('sortField') as SortField) || 'beds',
      sortOrder: (params.get('sortOrder') as SortOrder) || 'asc',
    };
  });

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.prop-fp-dropdown-wrap')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleDropdown = (name: string) => {
    setOpenDropdown(prev => prev === name ? null : name);
  };

  const filteredPlans = useMemo(() => {
    let plans = [...allFloorPlans];

    if (!isFullPriceRange) {
      plans = plans.filter((fp: any) => {
        const raw = fp.floorplan_price_min;
        if (raw == null || raw === '') return true;
        const p = parseFloat(String(raw).replace(/[$,]/g, ''));
        if (!isFinite(p) || p <= 0) return true;
        return p >= priceRange[0] && p <= priceRange[1];
      });
    }
    if (filters.beds) {
      plans = plans.filter((fp: any) => String(fp.floorplan_beds_min) === filters.beds);
    }
    if (filters.baths) {
      plans = plans.filter((fp: any) => String(fp.floorplan_baths_min) === filters.baths);
    }

    plans.sort((a: any, b: any) => {
      let aVal: number;
      let bVal: number;
      if (filters.sortField === 'beds') {
        const aBeds = Number(a.floorplan_beds_min || a.beds || 0);
        const bBeds = Number(b.floorplan_beds_min || b.beds || 0);
        const bedDiff = filters.sortOrder === 'asc' ? aBeds - bBeds : bBeds - aBeds;
        if (bedDiff !== 0) return bedDiff;
        // Secondary sort by baths (always same direction as primary)
        const aBaths = Number(a.floorplan_baths_min || a.baths || 0);
        const bBaths = Number(b.floorplan_baths_min || b.baths || 0);
        return filters.sortOrder === 'asc' ? aBaths - bBaths : bBaths - aBaths;
      } else if (filters.sortField === 'price') {
        aVal = parseFloat(String(a.floorplan_price_min || 0).replace(/[$,]/g, '')) || 0;
        bVal = parseFloat(String(b.floorplan_price_min || 0).replace(/[$,]/g, '')) || 0;
      } else {
        aVal = Number(a.floorplan_sqft_min || 0);
        bVal = Number(b.floorplan_sqft_min || 0);
      }
      return filters.sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return plans;
  }, [allFloorPlans, filters, priceRange, isFullPriceRange]);

  return (
    <>
      {/* ── Filter Bar + Floor Plan Grid ─────────────────────────────── */}
      <section className="prop-fp-section">
        <div className="prop-fp-section-bg" />
        <div className="prop-al-container-main">
          {/* Filter Row */}
          <div className="prop-fp-filter-row">
            <div className="prop-fp-available-count">
              <span className="prop-fp-count">{filteredPlans.length}</span> Available Floor Plans
            </div>

            <div className="prop-fp-filters">
              {/* Price Filter */}
              <div className="prop-fp-dropdown-wrap">
                <button
                  className={`prop-fp-dropdown-btn${openDropdown === 'price' ? ' active' : ''}`}
                  type="button"
                  onClick={() => toggleDropdown('price')}
                >
                  {!isFullPriceRange && <span className="prop-fp-filter-dot" />}
                  <FontAwesomeIcon icon={faDollarSign} className="prop-fp-btn-icon" />
                  <span>{isFullPriceRange ? 'Price' : `$${priceRange[0].toLocaleString()} – $${priceRange[1].toLocaleString()}`}</span>
                  <FontAwesomeIcon icon={faChevronDown} className="prop-fp-chevron" />
                </button>
                {openDropdown === 'price' && (
                  <div className="prop-fp-dropdown-menu prop-dropdown-price">
                    <div className="prop-fp-filter-title">Price</div>
                    <div className="prop-fp-filter-body">
                      <div className="prop-price-labels">
                        <span>${priceRange[0].toLocaleString()}</span>
                        <span>${priceRange[1].toLocaleString()}</span>
                      </div>
                      {priceMin < priceMax ? (
                        <RangeSlider
                          min={priceMin}
                          max={priceMax}
                          step={50}
                          value={priceRange}
                          onChange={(v) => setPriceRange(v)}
                        />
                      ) : (
                        <div style={{ fontSize: 13, color: '#666', textAlign: 'center', padding: '8px 0' }}>
                          ${priceMin.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Beds Filter */}
              <div className="prop-fp-dropdown-wrap">
                <button
                  className={`prop-fp-dropdown-btn${openDropdown === 'beds' ? ' active' : ''}`}
                  type="button"
                  onClick={() => toggleDropdown('beds')}
                >
                  {filters.beds !== '' && <span className="prop-fp-filter-dot" />}
                  <FontAwesomeIcon icon={faBed} className="prop-fp-btn-icon" />
                  <span>Beds</span>
                  <FontAwesomeIcon icon={faChevronDown} className="prop-fp-chevron" />
                </button>
                {openDropdown === 'beds' && (
                  <div className="prop-fp-dropdown-menu">
                    <div className="prop-fp-filter-title">Beds</div>
                    <div className="prop-fp-filter-body">
                      <div className="prop-fp-filter-options">
                        <label className="prop-fp-radio">
                          <input type="radio" name="beds" value="" checked={filters.beds === ''} onChange={() => setFilters(f => ({ ...f, beds: '' }))} />
                          <span>Any</span>
                        </label>
                        {allBeds.map(b => (
                          <label key={b} className="prop-fp-radio">
                            <input type="radio" name="beds" value={String(b)} checked={filters.beds === String(b)} onChange={() => setFilters(f => ({ ...f, beds: String(b) }))} />
                            <span>{b} Bed</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bathrooms Filter */}
              <div className="prop-fp-dropdown-wrap">
                <button
                  className={`prop-fp-dropdown-btn${openDropdown === 'baths' ? ' active' : ''}`}
                  type="button"
                  onClick={() => toggleDropdown('baths')}
                >
                  {filters.baths !== '' && <span className="prop-fp-filter-dot" />}
                  <FontAwesomeIcon icon={faBath} className="prop-fp-btn-icon" />
                  <span>Bathrooms</span>
                  <FontAwesomeIcon icon={faChevronDown} className="prop-fp-chevron" />
                </button>
                {openDropdown === 'baths' && (
                  <div className="prop-fp-dropdown-menu">
                    <div className="prop-fp-filter-title">Bathrooms</div>
                    <div className="prop-fp-filter-body">
                      <div className="prop-fp-filter-options">
                        <label className="prop-fp-radio">
                          <input type="radio" name="baths" value="" checked={filters.baths === ''} onChange={() => setFilters(f => ({ ...f, baths: '' }))} />
                          <span>Any</span>
                        </label>
                        {allBaths.map(b => (
                          <label key={b} className="prop-fp-radio">
                            <input type="radio" name="baths" value={String(b)} checked={filters.baths === String(b)} onChange={() => setFilters(f => ({ ...f, baths: String(b) }))} />
                            <span>{b} Bath</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sort Filter */}
              <div className="prop-fp-dropdown-wrap">
                <button
                  className={`prop-fp-dropdown-btn${openDropdown === 'sort' ? ' active' : ''}`}
                  type="button"
                  onClick={() => toggleDropdown('sort')}
                >
                  <span>Sort by</span>
                  <FontAwesomeIcon icon={faChevronDown} className="prop-fp-chevron" />
                </button>
                {openDropdown === 'sort' && (
                  <div className="prop-fp-dropdown-menu">
                    <div className="prop-fp-filter-title">Sort</div>
                    <div className="prop-fp-filter-body">
                      <div className="prop-fp-filter-box">
                        <div className="prop-fp-filter-label">Order</div>
                        <div className="prop-fp-filter-options">
                          <label className="prop-fp-radio">
                            <input type="radio" name="sortOrder" value="asc" checked={filters.sortOrder === 'asc'} onChange={() => setFilters(f => ({ ...f, sortOrder: 'asc' }))} />
                            <span>Ascending</span>
                          </label>
                          <label className="prop-fp-radio">
                            <input type="radio" name="sortOrder" value="desc" checked={filters.sortOrder === 'desc'} onChange={() => setFilters(f => ({ ...f, sortOrder: 'desc' }))} />
                            <span>Descending</span>
                          </label>
                        </div>
                      </div>
                      <div className="prop-fp-filter-box">
                        <div className="prop-fp-filter-label">Sort By</div>
                        <div className="prop-fp-filter-options">
                          <label className="prop-fp-radio">
                            <input type="radio" name="sortField" value="beds" checked={filters.sortField === 'beds'} onChange={() => setFilters(f => ({ ...f, sortField: 'beds' }))} />
                            <span>Bedrooms</span>
                          </label>
                          <label className="prop-fp-radio">
                            <input type="radio" name="sortField" value="price" checked={filters.sortField === 'price'} onChange={() => setFilters(f => ({ ...f, sortField: 'price' }))} />
                            <span>Price</span>
                          </label>
                          <label className="prop-fp-radio">
                            <input type="radio" name="sortField" value="sqft" checked={filters.sortField === 'sqft'} onChange={() => setFilters(f => ({ ...f, sortField: 'sqft' }))} />
                            <span>Square Feet</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Floor Plan Grid - 4-column on large screens */}
          <div className="prop-fp-grid">
            {filteredPlans.map((fp: any, i: number) => {
              const planName = fp.floorplan_name || fp.name || `Plan ${i + 1}`;
              const sqftRaw = fp.floorplan_sqft_min || fp.sqft;
              const sqftNum = sqftRaw ? Number(String(sqftRaw).replace(/[^0-9.]/g, '')) : NaN;
              const sqftDisplay = isFinite(sqftNum) && sqftNum > 0 ? `${sqftNum.toLocaleString()} sq.ft.` : '';
              const rentMin = fp.floorplan_price_min || fp.rent_min;
              const image = fp.floorplan_image_url || fp.image;

              return (
                <div key={fp.id || i} className="prop-fp-card">
                  {/* Head: name + sqft + price */}
                  <div className="prop-fp-card-head">
                    <h3 className="prop-fp-card-name">{planName}</h3>
                    {sqftDisplay && <div className="prop-fp-card-sqft">{sqftDisplay}</div>}
                    {rentMin != null && rentMin !== '' ? (
                      <div className="prop-fp-card-price-row">
                        Starting at <span className="prop-fp-card-price">${Number(rentMin).toLocaleString()}</span>/month
                      </div>
                    ) : null}
                  </div>

                  {/* Body: image + disclaimer */}
                  <div className="prop-fp-card-body">
                    <div className="prop-fp-card-image">
                      {image ? (
                        <VibesitesImage url={image} alt={planName} sizes="(min-width: 1400px) 25vw, (min-width: 1200px) 33vw, (min-width: 992px) 50vw, 100vw" />
                      ) : (
                        <div className="prop-fp-card-image-placeholder" />
                      )}
                    </div>
                    <div className="prop-fp-card-disclaimer">
                      <p>Price and availability are subject to change. For information about handicap accessibility, please contact the office.</p>
                    </div>
                  </div>

                  {/* Footer: CTAs */}
                  <div className="prop-fp-card-footer">
                    {cardApplyCta.cta_url ? (
                      <EditableSlotGroup groupPath="propertyfloorplanslist.card_apply_cta" definition={FP_SLOT_DEFS['propertyfloorplanslist.card_apply_cta']}>
                        <a className="prop-btn prop-btn-sm" href={cardApplyCta.cta_url} target={cardApplyCta._target || '_self'} rel={cardApplyCta._target === '_blank' ? 'noopener noreferrer' : undefined} data-vibesite-event={cardApplyCta.vibesite_event || undefined}>
                          {cardApplyCta.cta_text || 'Apply Online'}
                        </a>
                      </EditableSlotGroup>
                    ) : phone ? (
                      <div className="prop-fp-call-pricing">
                        <div className="prop-fp-call-label">Call for details</div>
                        <a href={`tel:${phone.replace(/\D/g, '')}`} className="prop-fp-call-link">
                          {phone}
                        </a>
                      </div>
                    ) : null}
                    <EditableSlotGroup groupPath="propertyfloorplanslist.card_tour_cta" definition={FP_SLOT_DEFS['propertyfloorplanslist.card_tour_cta']}>
                      {cardTourCta.cta_url ? (
                        <a className="prop-btn prop-btn-sm prop-btn-border" href={cardTourCta.cta_url} target={cardTourCta._target || '_self'} rel={cardTourCta._target === '_blank' ? 'noopener noreferrer' : undefined} data-vibesite-event={cardTourCta.vibesite_event || undefined}>
                          {cardTourCta.cta_text || 'Schedule a Tour'}
                        </a>
                      ) : (
                        <button type="button" className="prop-btn prop-btn-sm prop-btn-border" onClick={openTourScheduler}>
                          {cardTourCta.cta_text || 'Schedule a Tour'}
                        </button>
                      )}
                    </EditableSlotGroup>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredPlans.length === 0 && (
            <div className="prop-fp-no-results">No floor plans match your filters. <button type="button" onClick={() => { setPriceRange([priceMin, priceMax]); setFilters({ minPrice: '', maxPrice: '', beds: '', baths: '', sortField: 'beds', sortOrder: 'asc' }); }}>Clear filters</button></div>
          )}
        </div>
      </section>

      <FlexPaymentBanner />

      {/* ── Site Plan Section ─────────────────────────────────────────── */}
      {/* Background image, title, and CTA are SIBLING editable regions — never nested.
          Nesting an EditableSlotGroup inside another makes the outer group's
          capture-phase click handler swallow clicks meant for the inner one. */}
      <section className="prop-fp-site-plan-section">
        <EditableSlotGroup
          groupPath="propertyfloorplanslist.site_plan_bg"
          definition={FP_SLOT_DEFS['propertyfloorplanslist.site_plan_bg']}
          className="prop-fp-site-plan-bg"
        >
          {sitePlanBg.image && (
            <VibesitesImage url={sitePlanBg.image} alt="" sizes="100vw" />
          )}
        </EditableSlotGroup>
        <div className="prop-fp-site-plan-overlay" />
        <div className="prop-fp-site-plan-inner" style={{ color: '#fff' }}>
          {logo && <VibesitesImage url={logo} alt={`${name} logo`} className="prop-fp-site-plan-logo" />}
          <EditableToken path="propertyfloorplanslist.site_plan_title" type="text" label="Site Plan Title" as="h3" style={{ color: '#fff' }} defaultValue={contentSchema.tokens['propertyfloorplanslist.site_plan_title'].defaultValue}>
            {sitePlanTitle}
          </EditableToken>
          <div className="prop-al-separator" style={{ borderColor: 'rgba(255,255,255,0.4)', margin: '1rem auto' }} />
          {(sitePlanCta.cta_url || `${basePath}/site-plan/`) && (
            <EditableSlotGroup groupPath="propertyfloorplanslist.site_plan_cta" definition={FP_SLOT_DEFS['propertyfloorplanslist.site_plan_cta']}>
              <a className="prop-btn prop-fp-site-plan-btn" href={sitePlanCta.cta_url || `${basePath}/site-plan/`} target={sitePlanCta._target || '_self'} rel={sitePlanCta._target === '_blank' ? 'noopener noreferrer' : undefined} data-vibesite-event={sitePlanCta.vibesite_event || undefined}>
                <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: '0.5rem', color: '#fff' }} />
                {sitePlanCta.cta_text || 'View Our Property Map'}
              </a>
            </EditableSlotGroup>
          )}
        </div>
      </section>

      {/* ── 1-Bedroom Section ─────────────────────────────────────────── */}
      {has1Bed && (
        <PropertyContentSection
          variant="dark"
          className="prop-fp-bed1-section"
          overlay={
            <div className="prop-fp-bed1-overlay" role="presentation" aria-hidden="true">
              <img src={caseVerticalIcon} alt="background icon of case & associates" />
            </div>
          }
          left={
            <div className="prop-al-images-col" style={{ position: 'relative' }}>
              <img src={cornerIcon} alt="" className="prop-corner-icon prop-corner-icon--tl" aria-hidden="true" />
              <EditableSlotGroup groupPath="propertyfloorplanslist.bedroom1_image" definition={FP_SLOT_DEFS['propertyfloorplanslist.bedroom1_image']}>
                <div className="prop-al-single-image">
                  <VibesitesImage url={bed1Image.image || undefined} alt={bed1Image.alt || ''} sizes="(min-width: 992px) 50vw, 100vw" />
                </div>
              </EditableSlotGroup>
            </div>
          }
          right={
            <div className="prop-al-text-box">
              <RichTextContent path="propertyfloorplanslist.bed1_title" label="1-Bedroom Title" defaultValue={contentSchema.tokens['propertyfloorplanslist.bed1_title'].defaultValue} as="h2" className="wp-block-heading prop-fp-bedroom-title" />
              <div className="prop-al-separator" />
              <RichTextContent path="propertyfloorplanslist.bed1_blurb" label="1-Bedroom Description" defaultValue={contentSchema.tokens['propertyfloorplanslist.bed1_blurb'].defaultValue} as="div" className="prop-al-blurb" />
              {bed1CtaUrl && (
                <EditableToken path="propertyfloorplanslist.bed1_cta_url" type="text" label="1-Bedroom CTA URL" as="span" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['propertyfloorplanslist.bed1_cta_url'].defaultValue}>
                  <EditableToken path="propertyfloorplanslist.bed1_cta_text" type="text" label="1-Bedroom CTA Text" as="span" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['propertyfloorplanslist.bed1_cta_text'].defaultValue}>
                    <a className="prop-btn" href={bed1CtaUrl}>{bed1CtaText}</a>
                  </EditableToken>
                </EditableToken>
              )}
              <EditableToken path="propertyfloorplanslist.faq_title" type="text" label="FAQ Section Title" as="span" style={{ display: 'none' }}>
                {'' /* faq_title token placeholder */}
              </EditableToken>
            </div>
          }
        />
      )}

      {/* ── 2-Bedroom Section (centered full-width) ───────────────────── */}
      {has2Bed && (
        <section className="prop-fp-bedroom-section prop-fp-bedroom-section--center prop-al-bg-dark">
          <div className="prop-al-container-main">
            <div className="prop-fp-bedroom-center-inner">
              <RichTextContent path="propertyfloorplanslist.bed2_title" label="2-Bedroom Title" defaultValue={contentSchema.tokens['propertyfloorplanslist.bed2_title'].defaultValue} as="h2" className="wp-block-heading prop-fp-bedroom-title" />
              <RichTextContent path="propertyfloorplanslist.bed2_blurb" label="2-Bedroom Description" defaultValue={contentSchema.tokens['propertyfloorplanslist.bed2_blurb'].defaultValue} as="div" className="prop-al-blurb" />
            </div>
          </div>
        </section>
      )}

      {/* ── 3-Bedroom Section ─────────────────────────────────────────── */}
      {has3PlusBed && (
        <PropertyContentSection
          variant="dark"
          reverse
          left={
            <div className="prop-al-text-box" style={{ position: 'relative', overflow: 'hidden' }}>
              <img
                src={caseBackgroundIcon}
                alt=""
                aria-hidden="true"
                className="prop-fp-bed3-bg-icon"
              />
              <RichTextContent path="propertyfloorplanslist.bed3_title" label="3-Bedroom Title" defaultValue={contentSchema.tokens['propertyfloorplanslist.bed3_title'].defaultValue} as="h2" className="wp-block-heading prop-fp-bedroom-title" style={{ position: 'relative', zIndex: 1 }} />
              <div className="prop-al-separator" style={{ position: 'relative', zIndex: 1 }} />
              <RichTextContent path="propertyfloorplanslist.bed3_blurb" label="3-Bedroom Description" defaultValue={contentSchema.tokens['propertyfloorplanslist.bed3_blurb'].defaultValue} as="div" className="prop-al-blurb" style={{ position: 'relative', zIndex: 1 }} />
              <EditableSlotGroup groupPath="propertyfloorplanslist.bed3_cta" definition={FP_SLOT_DEFS['propertyfloorplanslist.bed3_cta']}>
                {bed3Cta.cta_url ? (
                  <a className="prop-btn" href={bed3Cta.cta_url} target={bed3Cta._target || '_self'} rel={bed3Cta._target === '_blank' ? 'noopener noreferrer' : undefined} data-vibesite-event={bed3Cta.vibesite_event || undefined} style={{ position: 'relative', zIndex: 1 }}>
                    {bed3Cta.cta_text || 'Schedule a Tour'}
                  </a>
                ) : (
                  <button type="button" className="prop-btn" onClick={openTourScheduler} style={{ position: 'relative', zIndex: 1 }}>
                    {bed3Cta.cta_text || 'Schedule a Tour'}
                  </button>
                )}
              </EditableSlotGroup>
            </div>
          }
          right={
            <div className="prop-al-images-col" style={{ position: 'relative' }}>
              <img src={cornerIcon} alt="" className="prop-corner-icon prop-corner-icon--tr" aria-hidden="true" />
              <EditableSlotGroup groupPath="propertyfloorplanslist.bedroom3_image" definition={FP_SLOT_DEFS['propertyfloorplanslist.bedroom3_image']}>
                <div className="prop-al-single-image">
                  <VibesitesImage url={bed3Image.image || undefined} alt={bed3Image.alt || ''} sizes="(min-width: 992px) 50vw, 100vw" />
                </div>
              </EditableSlotGroup>
            </div>
          }
        />
      )}

    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyFloorplansList',
  displayName: 'Property Floor Plans Full Page',
  tokens: {
    'propertyfloorplanslist.bed1_title': {
      label: '1-Bedroom Section Title',
      type: 'richtext',
      defaultValue: '1-Bedroom Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>',
    },
    'propertyfloorplanslist.bed1_blurb': {
      label: '1-Bedroom Section Text',
      type: 'richtext',
      defaultValue: 'Our <span data-type="field-token" data-field="property_address_city"></span> 1-bedroom apartments, townhouses, and lofts feature ample living room areas with plenty of space for plush couches, coffee tables, and personalized furnishings.',
    },
    'propertyfloorplanslist.bed1_cta_text': {
      label: '1-Bedroom CTA Text',
      type: 'text',
      defaultValue: '3D Virtual Tour',
    },
    'propertyfloorplanslist.bed1_cta_url': {
      label: '1-Bedroom CTA URL',
      type: 'text',
      defaultValue: '',
    },
    'propertyfloorplanslist.bed2_title': {
      label: '2-Bedroom Section Title',
      type: 'richtext',
      defaultValue: '2-Bedroom Apartments in <span data-type="field-token" data-field="property_address_city"></span>',
    },
    'propertyfloorplanslist.bed2_blurb': {
      label: '2-Bedroom Section Text',
      type: 'richtext',
      defaultValue: 'Each of our 2-bedroom floor plans offers over 1,000 sq. ft. of room, a spacious kitchen, and bright, airy rooms with large windows. Your kitchen, living room, and dining room all seamlessly flow into one another, opening up room for furniture and decor.',
    },
    'propertyfloorplanslist.bed3_title': {
      label: '3-Bedroom Section Title',
      type: 'richtext',
      defaultValue: '3-Bedroom Apartments in <span data-type="field-token" data-field="property_address_city"></span>',
    },
    'propertyfloorplanslist.bed3_blurb': {
      label: '3-Bedroom Section Text',
      type: 'richtext',
      defaultValue: "The moment you enter your 3-bedroom apartment, you'll be welcomed by your full chef's kitchen and dining area, complete with ample cabinetry and countertop space. Kick back and relax in your living area, with plush carpeting and large windows letting in natural light.",
    },
    'propertyfloorplanslist.faq_title': {
      label: 'FAQ Section Title',
      type: 'text',
      defaultValue: '',
    },
    'propertyfloorplanslist.site_plan_title': {
      label: 'Site Plan Title',
      type: 'text',
      defaultValue: '',
      description: 'Defaults to "{property_name} Site Plan"',
    },
  },
  slotGroups: FP_SLOT_DEFS,
  collectionBindings: {
    'propertyfloorplanslist.plans': {
      label: 'Floor Plans',
      collection: 'floorplans',
      description: 'All floor plan items',
    } as CollectionBindingDefinition,
  },
};

(PropertyFloorplansList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyFloorplansList;
